import { useEffect, type ReactNode } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints, APIError } from "@/lib/api";
import Layout from "@/components/Layout";
import Login from "@/routes/Login";
import Dashboard from "@/routes/Dashboard";
import EvaluationHub from "@/routes/EvaluationHub";
import Channels from "@/routes/Channels";
import ChannelDetail from "@/routes/ChannelDetail";
import Entities from "@/routes/Entities";
import EntityDetail from "@/routes/EntityDetail";
import EntitySelf from "@/routes/EntitySelf";
import SponsorshipReview from "@/routes/SponsorshipReview";
import Campaigns from "@/routes/Campaigns";
import CampaignDetail from "@/routes/CampaignDetail";
import CampaignReport from "@/routes/CampaignReport";
import Plans from "@/routes/Plans";
import PlanDetail from "@/routes/PlanDetail";
import Exchanges from "@/routes/Exchanges";
import ExchangeDetail from "@/routes/ExchangeDetail";
import CSVImport from "@/routes/CSVImport";
import Discovery from "@/routes/Discovery";
import Keywords from "@/routes/Keywords";
import AuditLog from "@/routes/AuditLog";
import Settings from "@/routes/Settings";
import System from "@/routes/System";
import XAccounts from "@/routes/XAccounts";
import YouTubeCookies from "@/routes/YouTubeCookies";

// AuthGate probes /api/health (public) for connectivity, then a protected
// endpoint to detect whether the cookie is still valid. On 401 it bounces
// the user to /login.
function AuthGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const probe = useQuery({
    queryKey: ["auth-probe"],
    queryFn: () => endpoints.evaluationTasks("scored"),
    retry: false,
  });

  useEffect(() => {
    if (probe.isError && probe.error instanceof APIError && probe.error.status === 401) {
      qc.clear();
      navigate("/login", { replace: true, state: { from: location.pathname } });
    }
  }, [probe.isError, probe.error, navigate, location.pathname, qc]);

  if (probe.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        加载中…
      </div>
    );
  }
  if (probe.isError) {
    // Network / server down: render children anyway so internal errors are
    // visible. 401 is already handled by the effect above.
    return <>{children}</>;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <AuthGate>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/evaluation" element={<EvaluationHub />} />
                <Route path="/channels" element={<Channels />} />
                <Route path="/channels/:id" element={<ChannelDetail />} />
                <Route path="/entities" element={<Entities />} />
                {/* React Router v6 ranks static segments above param
                    segments by specificity, so /entities/self wins over
                    /entities/:id regardless of order. We list it first
                    for human readability, not correctness. */}
                <Route path="/entities/self" element={<EntitySelf />} />
                <Route path="/entities/:id" element={<EntityDetail />} />
                <Route path="/sponsorships" element={<SponsorshipReview />} />
                <Route path="/discovery" element={<Discovery />} />
                <Route path="/keywords" element={<Keywords />} />
                <Route path="/audit" element={<AuditLog />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/campaigns" element={<Campaigns />} />
                <Route path="/campaigns/:id" element={<CampaignDetail />} />
                <Route path="/campaigns/:id/report" element={<CampaignReport />} />
                <Route path="/plans" element={<Plans />} />
                <Route path="/plans/:id" element={<PlanDetail />} />
                <Route path="/exchanges" element={<Exchanges />} />
                <Route path="/exchanges/:id" element={<ExchangeDetail />} />
                <Route path="/import" element={<CSVImport />} />
                <Route path="/system" element={<System />} />
                <Route path="/x-accounts" element={<XAccounts />} />
                <Route path="/youtube-cookies" element={<YouTubeCookies />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </AuthGate>
        }
      />
    </Routes>
  );
}
